import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';
import {
  Button,
  ButtonLink,
  Input,
  Modal,
  ModalSettingsShape,
  Toggle,
} from '@rabbit/elements/shared-components';
import { LIST_CURRENCIES } from '@rabbit/bizproc/react';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import {
  getDefaultAddress,
  getPartnerLabelByValue,
  getPartnerOptions,
} from '@rabbit/sage/utils/helpers';
import {
  AddressModalKind,
  EditAddAddressModal,
} from '../../molecules/account-management/EditAddAddressModal/EditAddAddressModal';
import {
  Address,
  BusinessUserInviteType,
  PersonaTypeSingleLetter,
  BusinessUserInviteStatus,
  DTBusiness_User_Invite,
  DTMainGroup,
} from '@rabbit/data/types';
import { useTranslation } from 'react-i18next';
import { SAGE_ROUTE_NAME } from '@rabbit/config/enums';
import { SelectOptionShape } from '@rabbit/elements/shared-types';
import { AppContext } from '@rabbit/app-context';
import {
  nestApiGetAllMainGroups,
  nestApiUpdateBusinessUserInvite,
} from '@rabbit/bizproc/core';
import { supagardBrandCategories } from '../ModalNewEditPartner/ModalNewEditPartner';

interface FormValuesShape {
  partner_name: string;
  partner_email: string;
  partner_type: PersonaTypeSingleLetter[];
  partner_phone_number: string;
  partner_phone_number_e164?: string;
  partner_addresses?: Address[];
  increased_labour_rate: boolean;
  labour_rate?: { amount: string; currency: string | undefined } | undefined;
  labour_rate_tier_1?: { amount: string; currency: string };
  labour_rate_tier_2?: { amount: string; currency: string };
  labour_rate_tier_3?: { amount: string; currency: string };
  termsAndConditions: boolean;
  salesperson_code?: string;
  account_number?: string;
  main_group?: string;
  brands?: string[];
  resend_mail?: boolean;
}

export interface ModalEditPartnerBusinessUserInviteProps {
  handleClose: () => void;
  handleDelete: () => void;
  onSuccess?: () => void;
  partner?: DTBusiness_User_Invite;
  modalSettings: ModalSettingsShape;
  installerSettings?: any;
}

interface MainGroup {
  id: string;
  label: string;
  value: string;
  associatedPartners: any;
  associatedBrands: string;
  sharedLocation: any;
}

const inputTypeCurrencyOverrideOptions: Array<SelectOptionShape> =
  LIST_CURRENCIES.map((currency, index) => ({
    id: `${index}`,
    label: currency.code + '/h',
    value: currency.code,
  }));

export function ModalEditPartnerBusinessUserInvite({
  handleClose,
  handleDelete,
  onSuccess,
  partner,
  modalSettings,
  installerSettings = {},
}: ModalEditPartnerBusinessUserInviteProps) {
  const navigate = useNavigate();
  const formRef = useRef(null) as any;
  const { config, tenantInfo } = useContext(AppContext);
  const isTcRequired = useMemo(
    () => config.PARTNERS.REGISTRATION.TC_REQUIRED,
    [config]
  );
  const readPartner: any | undefined = partner;

  const isInstaller = installerSettings.installer;
  const [isLoading, setIsLoading] = useState(false);
  const [addressModal, setAddressModal] = useState<
    'edit' | 'new' | undefined
  >();

  const { t } = useTranslation();
  const tenantLink = t('tenantLink');

  const [currentAddresses, setCurrentAddresses] = useState<Address[]>(
    partner?.addresses || []
  );
  const [defaultAddress, setDefaultAddress] = useState<Address | undefined>(
    partner?.addresses ? getDefaultAddress(partner.addresses) : undefined
  );
  const [mainGroups, setMainGroups] = useState<MainGroup[]>([]);
  const [isLoadingMainGroups, setIsLoadingMainGroups] = useState(false);
  const [isEmailOptional, setIsEmailOptional] = useState(false);

  const updateAddresses = (addresses: Address[]) => {
    setCurrentAddresses(addresses);
    const defaultAddr = getDefaultAddress(addresses);
    setDefaultAddress(defaultAddr);
    formRef.current.setFieldValue('partner_addresses', addresses);
  };

  const onAddressAdded = async (
    address: Address,
    mode: 'edit' | 'new',
    kind: AddressModalKind
  ) => {
    if (kind === 'partner') {
      if (mode === 'new') {
        const newAddresses = [...currentAddresses, address];
        updateAddresses(newAddresses);
      }
      if (mode === 'edit') {
        const newAddresses = currentAddresses.filter((item: any) => {
          return item.id !== address.id;
        });
        newAddresses.push(address);

        if (address.isDefault) {
          newAddresses.forEach((item: any) => {
            if (item.id !== address.id) {
              item.isDefault = false;
            }
          });
        }
        updateAddresses(newAddresses);
      }
    }
  };

  const PinnacleLabourRatesSchema = {
    labour_rate: Yup.object({
      amount: Yup.number()
        .required(t('message.pleaseEnterLabourRate'))
        .min(1, t('message.amountMustBeBiggerThanZero')),
      currency: Yup.string().required(t('message.pleaseSelectCurrency')),
    }),
    labour_rate_tier_1: Yup.object({
      amount: Yup.number().min(1, t('message.amountMustBeBiggerThanZero')),
      currency: Yup.string(),
    }),
    labour_rate_tier_2: Yup.object({
      amount: Yup.number().min(1, t('message.amountMustBeBiggerThanZero')),
      currency: Yup.string(),
    }),
    labour_rate_tier_3: Yup.object({
      amount: Yup.number().min(1, t('message.amountMustBeBiggerThanZero')),
      currency: Yup.string(),
    }),
  };

  const SupagardDealerSchema = {
    account_number: Yup.string().required(t('message.accountNumber')),
    main_group: Yup.string().required(t('message.mainGroup')),
    salesperson_code: Yup.string().required(t('message.salespersonCode')),
    brands: Yup.array().min(1).required(t('message.brand')),
    resend_mail: Yup.boolean().optional().default(false),
  };

  const addressSchema = Yup.object().shape({
    line1: Yup.string().trim().required('Please enter an address.'),
    line2: Yup.string().trim(),
    label: Yup.string().trim().required('Please enter a label'),
    town: Yup.string().trim().required('Please enter a city / suburb.'),
    state: Yup.string().trim().required('Please enter a state / territory.'),
    postcode: Yup.string().trim().required('Please enter a post code.'),
    country: Yup.string().trim().required('Please enter a country.'),
    isDefault: Yup.bool().default(false),
  });

  const schema = Yup.object({
    partner_name: Yup.string().required(t('message.pleaseEnterAName')),
    partner_email: Yup.string()
      .trim()
      .required('Required')
      .email(t('message.pleaseEnterAValidEmailAddress')),
    partner_phone_number: Yup.string()
      .trim()
      .required(t('message.pleaseEnterAPhoneNumber')),
    partner_type: Yup.array().required(t('message.pleaseSelectAType')),
    increased_labour_rate: Yup.boolean(),
    termsAndConditions: isTcRequired
      ? Yup.bool()
          .oneOf([true], 'You must accept the Terms & Conditions')
          .required('You must accept the Terms & Conditions')
      : Yup.bool(),
    ...(tenantLink === 'PINNACLEWARRANTIES' && PinnacleLabourRatesSchema),
    ...(tenantLink === 'SUPAGARD' && SupagardDealerSchema),
    partner_addresses: config.PARTNERS.REGISTRATION.ADDRESS_REQUIRED
      ? Yup.array(addressSchema)
          .min(1)
          .required('Please enter at least one address.')
      : Yup.array(addressSchema),
  });

  const PinnacleLabourRatesInitValues = {
    labour_rate: { amount: '', currency: tenantInfo?.currency },
    labour_rate_tier_1: { amount: '', currency: tenantInfo?.currency },
    labour_rate_tier_2: { amount: '', currency: tenantInfo?.currency },
    labour_rate_tier_3: { amount: '', currency: tenantInfo?.currency },
  };
  // TODO: this might be slightly more complicated in the future. It's not even currently needed, but ehh. Hopefully it'll be useful in the future
  const partnerType = partner?.personas;

  const [initialValues, setInitialValues] = useState<any>({
    partner_name: partner?.orgName ?? '',
    partner_email: partner?.email || '',
    partner_phone_number: partner?.phone || '',
    partner_phone_number_e164: partner?.phone || '',
    partner_type: partnerType || '',
    labour_rate: {
      amount: '',
      currency: tenantInfo?.currency,
    },
    increased_labour_rate: false,
    termsAndConditions: false,
    ...(tenantLink === 'PINNACLEWARRANTIES' && PinnacleLabourRatesInitValues),
    ...(tenantLink === 'SUPAGARD' && {
      salesperson_code: partner?.salespersonCode ?? '',
      account_number: partner?.accountNumber ?? '',
      main_group: partner?.mainGroup ?? '',
      brands: partner?.brands ?? [],
      resend_mail: false,
    }),
    partner_addresses: currentAddresses,
  });

  const handleSubmit = async (values: FormValuesShape) => {
    setIsLoading(true);
    let email;
    if (t('tenantLink') === 'SUPAGARD') {
      if (!isEmailOptional) {
        email = values.partner_email;
      } else {
        email =
          values.partner_email.length > 0
            ? values.partner_email
            : `${t('tenantLink')}-${new Date().getTime()}@dummyiwarranty.co`;
      }
    } else {
      email = values.partner_email;
    }
    try {
      if (
        partner?.status === BusinessUserInviteStatus.DummyUser ||
        partner?.status === BusinessUserInviteStatus.Pending
      ) {
        const partnerData = {
          email,
          orgName: values.partner_name,
          phone:
            values.partner_phone_number_e164 ?? values.partner_phone_number,
          type: BusinessUserInviteType.Partner,
          personas: Array.isArray(values.partner_type)
            ? values.partner_type
            : [values.partner_type],
          addresses: currentAddresses,
          tenantLink: t('tenantLink'),
          firstName: partner?.firstName || '',
          lastName: partner?.lastName || '',
          status: BusinessUserInviteStatus.Pending,
          settings: [
            {
              partner: t('tenantLink'),
              labourRates: {
                default: values?.labour_rate?.amount
                  ? Number(values.labour_rate.amount)
                  : null,
                tier1: values.labour_rate_tier_1?.amount
                  ? Number(values.labour_rate_tier_1?.amount)
                  : null,
                tier2: values.labour_rate_tier_2?.amount
                  ? Number(values.labour_rate_tier_2?.amount)
                  : null,
                tier3: values.labour_rate_tier_3?.amount
                  ? Number(values.labour_rate_tier_3?.amount)
                  : null,
                currency: tenantInfo?.currency ?? 'GBP',
              },
            },
          ],
          ...(tenantLink === 'SUPAGARD' && {
            salespersonCode: values.salesperson_code,
            accountNumber: values.account_number,
            mainGroup: values.main_group,
            brands: values.brands,
          }),
        };

        if (!partner?.docid) {
          toast.error(t('message.somethingWentWrongPleaseTryAgain'));
          setIsLoading(false);
          return;
        }

        // Update the businessUserInvite record
        const res = await nestApiUpdateBusinessUserInvite({
          inviteData: {
            ...partnerData,
            docid: partner.docid,
            resendMail:
              partner.email !== values.partner_email
                ? true
                : values.resend_mail,
          },
          baseUrl: window.location.origin,
          emailParams: {
            from: tenantInfo?.emailInfo?.emailSender ?? '',
            business_name: tenantInfo?.name ?? '',
            business_logo: tenantInfo?.logo ?? '',
            main_template: tenantInfo?.emailInfo?.emailMainTemplate ?? '',
            partner_type:
              getPartnerLabelByValue(partnerData.personas[0], config) ?? '',
          },
          tenantLink: t('tenantLink'),
        });

        if (!res) {
          toast.error(t('message.somethingWentWrongPleaseTryAgain'));
          setIsLoading(false);
          return;
        }
      }

      if (onSuccess) {
        onSuccess();
      }
      toast.success(t('message.partnerUpdatedSuccessfully'));
      handleClose();
    } catch (error: any) {
      console.error(error);
      const errMsg = error?.response?.data.message;
      toast.error(t(errMsg) || t('message.somethingWentWrongPleaseTryAgain'));
    } finally {
      setIsLoading(false);
    }
  };

  const openTc = (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    e.preventDefault();
    window.open(`${SAGE_ROUTE_NAME.TERMS_AND_CONDITIONS}`, '_blank');
  };

  const addressRequired = config.PARTNERS.REGISTRATION.ADDRESS_REQUIRED;

  const fetchMainGroups = async () => {
    try {
      const response: any = await nestApiGetAllMainGroups();
      if (response) {
        setMainGroups(
          response.map((group: DTMainGroup) => ({
            id: group.docid,
            label: group.title,
            value: group.docid,
            associatedPartners: group?.associatedPartners,
            associatedBrands: group?.associatedPartnerBrands,
            sharedLocation: group?.sharedLocation,
          }))
        );
      }
    } catch (error) {
      console.error('Failed to fetch main groups:', error);
    }
  };

  useEffect(() => {
    fetchMainGroups();
  }, []);

  useEffect(() => {
    if (partner?.docid) {
      if (formRef.current) {
        formRef.current.setFieldValue('partner_type', partnerType);
      }
    }
  }, [partner]);

  return (
    <Modal
      kind="generic"
      settings={modalSettings}
      className="m-auto w-[724px] rounded-md border bg-white"
      isLoading={isLoading}
    >
      <Formik
        onSubmit={handleSubmit}
        initialValues={initialValues}
        validationSchema={
          isEmailOptional
            ? Yup.object({
                ...schema.fields,
                partner_email: Yup.string()
                  .trim()
                  .email(t('message.pleaseEnterAValidEmailAddress')),
              })
            : schema
        }
        validateOnChange={true}
        validateOnBlur={false}
        innerRef={formRef}
      >
        {(props) => (
          <Form className="mt-2 w-full px-5">
            <div className="w-full py-2">
              <div className="flex w-full gap-4">
                <Input
                  type="text"
                  label={`${t('general.companyName')}*`}
                  name="partner_name"
                  settings={{
                    id: 'name',
                    hint: '*required',
                  }}
                />
                <Input
                  type="email"
                  label={`${t('general.email')}`}
                  name="partner_email"
                  settings={{
                    id: 'email',
                    disabled: isInstaller,
                    hint:
                      t('tenantLink') === 'SUPAGARD' && isEmailOptional
                        ? ''
                        : '*required',
                  }}
                />
              </div>
            </div>
            <div className="flex w-full gap-4 py-2">
              <Input
                type="phone"
                name="partner_phone_number"
                label={`${t('general.phoneNumber')}*`}
                settings={{
                  placeholder: t('general.phoneNumber'),
                  hint: '*required',
                  disabled: isInstaller,
                }}
              />
              {!partner?.docid && (
                <Input
                  type="select"
                  name="partner_type"
                  label={`${t('general.type')}*`}
                  settings={{
                    id: 'partner_type',
                    placeholder: t('message.pleaseSelectAnOption'),
                    hint: '*required',
                    options: getPartnerOptions(config),
                    isMulti: false,
                  }}
                />
              )}
            </div>

            {t('tenantLink') === 'SUPAGARD' && (
              <div className="mt-4 grid grid-cols-2 gap-4">
                <Input
                  type="text"
                  name="account_number"
                  label={t('message.accountNumber')}
                  settings={{
                    id: 'account_number',
                    placeholder: t('message.accountNumber'),
                    hint: '*required',
                  }}
                />
                <Input
                  type="text"
                  name="salesperson_code"
                  label={t('message.salespersonCode')}
                  settings={{
                    id: 'salesperson_code',
                    placeholder: t('message.salespersonCode'),
                    hint: '*required',
                  }}
                />
                <Input
                  type="select"
                  name="main_group"
                  label={t('message.mainGroup')}
                  className="col-span-2"
                  settings={{
                    id: 'main_group',
                    placeholder: t('message.pleaseSelectAnOption'),
                    hint: '*required',
                    options: mainGroups,
                    onChange: (value: any) => {
                      const findMappedMainGroup = mainGroups.find(
                        (group: any) => group.value === value.value
                      );
                      const activePartnerExist =
                        findMappedMainGroup?.associatedPartners?.filter(
                          (partner: any) => partner.tenantLink.length > 0
                        );
                      const newIsEmailOptional =
                        findMappedMainGroup?.associatedPartners?.length > 0 &&
                        activePartnerExist.length > 0 &&
                        findMappedMainGroup?.sharedLocation;

                      setIsEmailOptional(newIsEmailOptional);

                      // Force form validation after state update
                      setTimeout(() => {
                        props.validateForm();
                        props.setFieldTouched('partner_email', true, false);
                      }, 0);
                    },
                  }}
                />
                {config.PARTNERS.REGISTRATION.PARTNER_BRAND_MAPPING && (
                  <Input
                    type="select"
                    name="brands"
                    label={t('message.brand')}
                    className="col-span-2"
                    settings={{
                      id: 'brands',
                      isMulti: true,
                      placeholder: t('message.pleaseSelectAnOption'),
                      hint: '*required',
                      options: supagardBrandCategories,
                    }}
                  />
                )}
              </div>
            )}
            <div>
              <ButtonLink
                label={
                  t('general.partnerAddress') + (addressRequired ? '*' : '')
                }
                className={
                  props.errors.partner_addresses
                    ? 'border-red-500 bg-red-100'
                    : ''
                }
                onClick={() =>
                  currentAddresses.length > 0
                    ? setAddressModal('edit')
                    : setAddressModal('new')
                }
              >
                {defaultAddress ? (
                  <div>
                    {defaultAddress?.line1}, {defaultAddress?.line2}{' '}
                    {defaultAddress?.town} {defaultAddress?.state}{' '}
                    {defaultAddress?.country} {defaultAddress?.postcode}
                  </div>
                ) : (
                  <div>{t('general.addAddress')}</div>
                )}
              </ButtonLink>
            </div>
            {tenantLink === 'PINNACLEWARRANTIES' && (
              <div className="mt-4 flex flex-col gap-4">
                <div className="grid grid-cols-2 gap-4">
                  <Input
                    type="currency"
                    name="labour_rate"
                    label={`${t('general.defaultLabourRate')}*`}
                    settings={{
                      id: 'labour_rate',
                      placeholder: t('general.amount'),
                      hint: '*required',
                      currency: tenantInfo?.currency,
                      inputTypeCurrencyOverrideOptions,
                    }}
                  />
                </div>
                <div>
                  <Input
                    type="checkbox"
                    name="increased_labour_rate"
                    settings={{
                      checkboxLabel: t('message.increaseLabourRate'),
                    }}
                  />
                </div>
                {props.values.increased_labour_rate && (
                  <div className="grid grid-cols-3 gap-4">
                    <Input
                      type="currency"
                      name="labour_rate_tier_1"
                      label="Tier 1"
                      settings={{
                        id: 'labour_rate_tier_1',
                        placeholder: t('general.amount'),
                        currency: tenantInfo?.currency,
                        inputTypeCurrencyOverrideOptions,
                      }}
                    />
                    <Input
                      type="currency"
                      name="labour_rate_tier_2"
                      label="Tier 2"
                      settings={{
                        id: 'labour_rate_tier_2',
                        placeholder: t('general.amount'),
                        currency: tenantInfo?.currency,
                        inputTypeCurrencyOverrideOptions,
                      }}
                    />
                    <Input
                      type="currency"
                      name="labour_rate_tier_3"
                      label="Tier 3"
                      settings={{
                        id: 'labour_rate_tier_3',
                        placeholder: t('general.amount'),
                        currency: tenantInfo?.currency,
                        inputTypeCurrencyOverrideOptions,
                      }}
                    />
                  </div>
                )}
              </div>
            )}

            {isTcRequired && (
              <div className="relative mt-4 flex items-center">
                <Input
                  type="checkbox"
                  settings={{ id: 'termsAndConditions' }}
                  name="termsAndConditions"
                  className="text-primary-600 focus:ring-primary-500 h-4 w-full rounded border-gray-300"
                />
                <label
                  htmlFor="termsAndConditions"
                  className="absolute left-5 top-0 ml-2 block text-sm text-gray-700"
                >
                  I have read{' '}
                  <a
                    href="#"
                    onClick={openTc}
                    className="text-blue-600 underline"
                  >
                    Terms & Conditions
                  </a>{' '}
                  and agree
                </label>
              </div>
            )}

            {config.PARTNERS.EDIT_PARTNER.RESEND_MAIL && (
              <div className="mt-5 flex w-full items-center gap-2 rounded-lg border border-gray-200 bg-gray-100 px-3 py-2">
                <div className="grow">{t('Resend invite')}</div>
                <Toggle
                  name="resend_mail"
                  checked={props.values?.resend_mail ?? false}
                  onChange={() => {
                    props.setFieldValue(
                      'resend_mail',
                      !props.values.resend_mail
                    );
                  }}
                />
              </div>
            )}

            <div className="mt-8 flex gap-5 py-2">
              <Button
                loading={isLoading}
                kind="primary"
                size="md"
                className="shrink-0 grow"
                type="submit"
                disabled={props.isSubmitting || !props.isValid}
                children={t('general.save')}
              />
            </div>
          </Form>
        )}
      </Formik>
      {addressModal && (
        <EditAddAddressModal
          handleClose={() => setAddressModal(undefined)}
          mode={addressModal}
          kind={'partner'}
          address={
            currentAddresses.length > 0
              ? currentAddresses[0]
              : {
                  line1: '',
                  line2: '',
                  town: '',
                  state: '',
                  postcode: '',
                  country: '',
                  isDefault: true,
                }
          }
          handleSubmit={onAddressAdded}
          handleDelete={async (address) =>
            updateAddresses(
              currentAddresses.filter((i: Address) => i.line1 !== address.line1)
            )
          }
        />
      )}
    </Modal>
  );
}
